import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * ThemeContext
 *
 * Manages the global theme state for switching between Classic and Session Planner aesthetics.
 * Persists user preference to localStorage.
 *
 * To disable the Session Planner global theme feature entirely:
 * Comment out the CSS import below.
 */

// ============================================
// SINGLE POINT OF REMOVAL
// Comment out this line to disable the feature
// ============================================
import '../styles/session-planner-global.css';

// Storage key following lrbrain-* convention
const STORAGE_KEY = 'lrbrain-theme';

// Available themes
export const THEMES = {
  CLASSIC: 'classic',
  SESSION_PLANNER: 'session-planner'
};

// Theme display names for UI
export const THEME_LABELS = {
  [THEMES.CLASSIC]: 'Classic',
  [THEMES.SESSION_PLANNER]: 'Notebook'
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage, defaulting to classic
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === THEMES.SESSION_PLANNER ? THEMES.SESSION_PLANNER : THEMES.CLASSIC;
    } catch {
      return THEMES.CLASSIC;
    }
  });

  // Apply theme class to body element
  useEffect(() => {
    if (theme === THEMES.SESSION_PLANNER) {
      document.body.classList.add('theme-session-planner');
    } else {
      document.body.classList.remove('theme-session-planner');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('theme-session-planner');
    };
  }, [theme]);

  // Persist theme preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // localStorage not available (e.g., private browsing)
    }
  }, [theme]);

  // Set a specific theme
  const setTheme = useCallback((newTheme) => {
    if (Object.values(THEMES).includes(newTheme)) {
      setThemeState(newTheme);
    }
  }, []);

  // Toggle between themes
  const toggleTheme = useCallback(() => {
    setThemeState(prev =>
      prev === THEMES.CLASSIC ? THEMES.SESSION_PLANNER : THEMES.CLASSIC
    );
  }, []);

  // Convenience boolean for checking active theme
  const isSessionPlannerTheme = theme === THEMES.SESSION_PLANNER;
  const isClassicTheme = theme === THEMES.CLASSIC;

  // Get current theme label
  const currentThemeLabel = THEME_LABELS[theme];

  const value = {
    // State
    theme,
    isSessionPlannerTheme,
    isClassicTheme,
    currentThemeLabel,

    // Actions
    setTheme,
    toggleTheme,

    // Constants
    THEMES
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
